/**
 * Lista de Candidatos Aprobados por RH del Área
 * Vista para que el jefe de área vea candidatos listos para entrevista final
 */

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Search,
  CheckCircle,
  Calendar,
  XCircle,
  AlertCircle,
} from "lucide-react";
import type { ApprovedCandidateByArea } from '@/types/hiringManagerInterview';
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { config } from '@/config/env'
import { Badge } from '@/shared/components/Badge'
import FilterSelect from "@/shared/components/FilterSelect";
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { NADIE_LISTO } from '@/features/rh/lib/candidatosParaAgendar'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface ApprovedCandidatesListProps {
  candidates: ApprovedCandidateByArea[];
  isLoading: boolean;
  onScheduleInterview: (candidateId: string) => void;
  onRejectCandidate: (candidateId: string, candidateName: string) => Promise<void> | void;
}

interface PuestoOption {
  id: number;
  nombre?: string;
  name?: string;
  activo?: boolean;
}

export const ApprovedCandidatesList = ({
  candidates,
  isLoading,
  onScheduleInterview,
  onRejectCandidate,
}: ApprovedCandidatesListProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSecondInterview, setSelectedSecondInterview] =
    useState<string>("");
  const [selectedRhStatus, setSelectedRhStatus] = useState<string>("");
  const [selectedVacante, setSelectedVacante] = useState<string>("");
  const [vacantes, setVacantes] = useState<PuestoOption[]>([]);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const canRequesterFinalDecision = userHasPermission(
    "rh.solicitudes_personal.base.create",
    user?.rbac?.effectivePermissions || [],
  );

  // Modal de confirmación
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: "reject";
    candidateId: string;
    candidateName: string;
  } | null>(null);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const loadVacantes = async () => {
      try {
        const response = await fetch(
          `${
            config.apiBaseUrl
          }/catalogos/puestos/public/all`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        );

        if (!response.ok) return;

        const puestosData = await response.json();
        const puestosArray = Array.isArray(puestosData)
          ? puestosData
          : puestosData.data || [];

        setVacantes(
          puestosArray.filter(
            (puesto: PuestoOption) =>
              puesto.activo !== false && Boolean(puesto.nombre || puesto.name),
          ),
        );
      } catch (error) {
        console.error("Error loading vacantes filter:", error);
      }
    };

    loadVacantes();
  }, []);

  // Filtrar candidatos
  const filteredCandidates = candidates.filter((candidate) => {
    const isActionableByStatus =
      candidate.candidateStatus === "pre_seleccionado" ||
      candidate.candidateStatus === "entrevista_solicitante";

    if (!isActionableByStatus) return false;

    const matchesSearch =
      candidate.candidateName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      candidate.candidateEmail
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      candidate.jobRequestTitle
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      candidate.areaName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSecondInterview =
      selectedSecondInterview === "" ||
      (selectedSecondInterview === "si" && candidate.requiresSecondInterview) ||
      (selectedSecondInterview === "no" && !candidate.requiresSecondInterview);

    const matchesStatus =
      selectedRhStatus === "" ||
      (selectedRhStatus === "aprobado" &&
        candidate.resultadoEntrevistaRh >= 7) ||
      (selectedRhStatus === "pendiente" && candidate.resultadoEntrevistaRh < 7);

    const matchesVacante =
      selectedVacante === "" || candidate.jobRequestTitle === selectedVacante;

    return (
      matchesSearch && matchesSecondInterview && matchesStatus && matchesVacante
    );
  });

  const getStatusBadge = (status: string, _score: number) => {
    if (status === "seleccionado") {
      return <Badge color="verde">Seleccionado</Badge>;
    }

    if (status === "pre_seleccionado" || status === "entrevista_solicitante") {
      return <Badge color="verde">Aprobado por RH</Badge>;
    }

    return <Badge color="amarillo">Pendiente</Badge>;
  };

  // Es un dato de la fila, no un estado: va como texto. Con pastilla parecia
  // un badge de estatus y competia con la columna de Estatus.
  const getPmProcessBadge = (requiresSecondInterview: boolean) => (
    <span className="text-sm text-gray-900">
      {requiresSecondInterview ? "Sí" : "No"}
    </span>
  );

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedSecondInterview("");
    setSelectedRhStatus("");
    setSelectedVacante("");
  };

  const handleConfirmAction = async () => {
    if (!confirmModal) return;

    try {
      setIsSubmittingAction(true);
      await onRejectCandidate(
        confirmModal.candidateId,
        confirmModal.candidateName,
      );
    } finally {
      setIsSubmittingAction(false);
      setConfirmModal(null);
    }
  };

  // A-01: silueta de la tabla; el spinner dejaba la pantalla en blanco.
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <EsqueletoTabla filas={6} columnas={7} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros con el patrón de almacén: una fila, sin etiquetas encima ni
          iconos dentro de los desplegables, que ahora son el componente
          compartido. El buscador se topa a 320px para que no coma la fila. */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en candidatos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
          />
        </div>

        <FilterSelect
          label="Requiere 2da entrevista"
          ancho="contenido"
          allLabel="Todas"
          options={[
            { value: 'si', label: 'Sí' },
            { value: 'no', label: 'No' },
          ]}
          value={selectedSecondInterview}
          onChange={setSelectedSecondInterview}
        />

        <FilterSelect
          label="Estatus"
          ancho="contenido"
          options={[
            { value: 'aprobado', label: 'Aprobado por RH' },
            { value: 'pendiente', label: 'Pendiente' },
          ]}
          value={selectedRhStatus}
          onChange={setSelectedRhStatus}
        />

        <FilterSelect
          label="Vacante"
          ancho="contenido"
          allLabel="Todas"
          options={vacantes.map((vacante) => {
            const nombreVacante = vacante.nombre || vacante.name || ''
            return { value: nombreVacante, label: nombreVacante }
          })}
          value={selectedVacante}
          onChange={setSelectedVacante}
        />

        <button
          type="button"
          onClick={handleClearFilters}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>

        <span className="ml-auto text-sm text-gray-500">
          {filteredCandidates.length} candidatos
        </span>
      </div>

      {/* Tabla de candidatos */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        {isLoading ? (
          <EsqueletoTabla filas={6} columnas={7} />
        ) : filteredCandidates.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay candidatos aprobados
            </h3>
            <p className="text-gray-600">
              {searchTerm ||
              selectedSecondInterview ||
              selectedRhStatus ||
              selectedVacante
                ? "No se encontraron candidatos con los filtros aplicados."
                : NADIE_LISTO}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Candidato
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Segunda entrevista
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                     Email
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Vacante
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Aprobado por
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estatus
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {filteredCandidates.map((candidate) => (
                  <tr key={candidate.candidateId} className="hover:bg-[#F4F7FB] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/candidate/${candidate.candidateId}`)
                        }
                        className="text-sm font-medium text-gray-900 text-left"
                      >
                        {candidate.candidateName}
                      </button>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        navigate(`/candidate/${candidate.candidateId}`)
                      }
                    >
                      {getPmProcessBadge(candidate.requiresSecondInterview)}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        navigate(`/candidate/${candidate.candidateId}`)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {candidate.candidateEmail}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        navigate(`/candidate/${candidate.candidateId}`)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {candidate.jobRequestTitle}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        navigate(`/candidate/${candidate.candidateId}`)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        Recursos Humanos
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        navigate(`/candidate/${candidate.candidateId}`)
                      }
                    >
                      {getStatusBadge(
                        candidate.candidateStatus,
                        candidate.resultadoEntrevistaRh,
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            if (openMenuId === candidate.candidateId) {
                              setOpenMenuId(null);
                              setMenuPosition(null);
                            } else {
                              const button =
                                e.currentTarget as HTMLButtonElement;
                              const rect = button.getBoundingClientRect();

                              setMenuPosition({
                                top: rect.bottom + window.scrollY + 8,
                                right:
                                  window.innerWidth -
                                  rect.right +
                                  window.scrollX,
                              });
                              setOpenMenuId(candidate.candidateId);
                            }
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1"
                        >
                          <span className="text-xl">⋮</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Menú desplegable usando Portal - Se renderiza fuera del flujo normal del DOM */}
      {openMenuId !== null &&
        menuPosition &&
        createPortal(
          <div
            className="absolute w-48 bg-white rounded-lg shadow-2xl border border-gray-200"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
              zIndex: 99999,
            }}
          >
            <div className="py-1">
              {(userHasPermission(
                "rh.entrevistas.base.create",
                user?.rbac?.effectivePermissions || [],
              ) ||
                canRequesterFinalDecision) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const candidateId = openMenuId;
                      if (candidateId) {
                        onScheduleInterview(candidateId);
                        setOpenMenuId(null);
                        setMenuPosition(null);
                      }
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4 text-blue-600" />
                    Programar entrevista
                  </button>
                )}
              {canRequesterFinalDecision && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const candidateId = openMenuId;
                    const candidate = candidates.find(
                      (c) => c.candidateId === candidateId,
                    );
                    if (candidateId && candidate) {
                      setOpenMenuId(null);
                      setMenuPosition(null);
                      setConfirmModal({
                        isOpen: true,
                        type: "reject",
                        candidateId,
                        candidateName: candidate.candidateName,
                      });
                    }
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4 text-red-600" />
                  Rechazar
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Modal de confirmación */}
      {confirmModal?.isOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100000] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setConfirmModal(null)}
            />
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirmar rechazo
                </h3>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {`¿Estás seguro de que deseas rechazar al candidato "${confirmModal.candidateName}"? Esta acción no se puede deshacer.`}
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmModal(null)}
                  disabled={isSubmittingAction}
                  className={estiloDeBoton('secundario')}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={isSubmittingAction}
                  className={estiloDeBoton('peligro')}
                >
                  {isSubmittingAction ? "Procesando..." : "Rechazar"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
